/**
 * Group policies (docs/adr/0037-group-policies.md): a group owner publishes and manages company
 * policies (ADR 0026) across the companies of her group from the one Policies page.
 *
 * Three kinds of route, all under `/company-group/policies`:
 *
 *   - READS across companies — `listGroupPolicies` (rows first, NO acknowledgement counts),
 *     `getGroupPolicyCounts` (the counts, a second call), `getCompanyAudienceOptions`;
 *   - a QUEUED RUN for the one heavy action, create-and-publish to several companies at once
 *     (`stageGroupPolicyUpload`, `startGroupPolicyRun`, `getGroupPolicyRun`,
 *     `retryGroupPolicyRunItem`, and the recent-activity list `listGroupPolicyRuns`). A run is
 *     either a NEW policy written once (`draft`) or an EXISTING policy copied to more companies
 *     (`source`: add a company, publish the next version of a copy, or link an existing one). The
 *     start request only validates and records the run; a worker does the per-company work and the
 *     page polls `getGroupPolicyRun`;
 *   - SYNCHRONOUS BULK ACTIONS on existing copies — `runGroupPolicyAction` (remind, archive,
 *     restore, publish draft: one small write per company, a result per company), plus
 *     `previewGroupPolicyReach` (how many people an audience reaches), `linkGroupPolicies` and
 *     `unlinkGroupPolicy` (set or clear the shared lineage id, nothing else changes);
 *   - MIRRORED single-company routes (`company*`) — every ADMIN route of `company-policy.contract.ts`
 *     again at `/companies/:orgId/policies/...`, so the existing detail page can act in another
 *     company without switching to it. They reuse the SAME body, query and response schemas (declared
 *     once, in company-policy.contract.ts); `company-group-policy.contract.test.ts` fails if the two
 *     drift apart.
 *
 * Rights are the ones of ADR 0034: every route needs `org.group:manage` to get in, and the API then
 * checks `policy:manage` LIVE in each company the call touches, as the caller's own account there.
 * Company ids in a request only CHOOSE AMONG the group's members; the API reads the membership
 * itself. Every route is kept out of the AI copilot (`ai: 'hidden'`): bulk cross-company publishing
 * is not a proposal a model should draft, and several bodies carry a whole file.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  acknowledgementCountsSchema,
  companyPolicyAudienceSchema,
  companyPolicyContract,
  companyPolicyDraftBodySchema,
  companyPolicySummarySchema,
  publishCompanyPolicyBodySchema,
  uploadCompanyPolicyDocumentBodySchema,
} from './company-policy.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** At most this many companies in one list, count call or run (same cap as Group access). */
export const GROUP_POLICY_MAX_COMPANIES = 50;
/** The staged PDF's ceiling, the same as a single policy's upload. */
export const GROUP_POLICY_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Base64 is 4 characters per 3 bytes; checked on the string before anything is decoded. */
const MAX_UPLOAD_BASE64_CHARS = Math.ceil((GROUP_POLICY_MAX_UPLOAD_BYTES * 4) / 3) + 4;

/**
 * Whether the caller can use the policies of one company right now:
 *   `ok`          — listed;
 *   `no_access`   — she has no account there, or it lacks company-wide `policy:manage`;
 *   `module_off`  — the policies module is off or the trial has ended there;
 *   `unavailable` — it could not be read in time (a slow or failing company); safe to retry.
 */
export const groupPolicyAccessSchema = z.enum(['ok', 'no_access', 'module_off', 'unavailable']);
export type GroupPolicyAccess = z.infer<typeof groupPolicyAccessSchema>;

/** The access states a company can be SKIPPED for (everything except `ok`). */
export const groupPolicySkipReasonSchema = groupPolicyAccessSchema.exclude(['ok']);
export type GroupPolicySkipReason = z.infer<typeof groupPolicySkipReasonSchema>;

/** A company of the group as the policies page sees it. */
export const groupPolicyCompanySchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  access: groupPolicyAccessSchema,
});
export type GroupPolicyCompany = z.infer<typeof groupPolicyCompanySchema>;

/** One company's copy of a policy: the usual summary, without acknowledgement progress. */
export const groupPolicyCopySchema = companyPolicySummarySchema.omit({ acknowledgements: true });
export type GroupPolicyCopy = z.infer<typeof groupPolicyCopySchema>;

/**
 * One row of the cross-company list. Copies made together share `lineageId` and are one row;
 * a policy that exists in a single company (or was never linked) is a row of one copy with
 * `lineageId` null. `key` is the lineage id, else the policy id — stable for React keys.
 */
export const groupPolicyRowSchema = z.object({
  key: z.string(),
  lineageId: uuidSchema.nullable(),
  /** From the first copy, for sorting and search; each copy keeps its own title. */
  title: z.string(),
  category: z.string(),
  copies: z.array(groupPolicyCopySchema).min(1),
});
export type GroupPolicyRow = z.infer<typeof groupPolicyRowSchema>;

export const listGroupPoliciesResponseSchema = z.object({
  /** Every company asked for, in the group's order, with whether its policies could be read. */
  companies: z.array(groupPolicyCompanySchema),
  /** Policies of the companies whose `access` is `ok`, grouped by lineage. */
  items: z.array(groupPolicyRowSchema),
});
export type ListGroupPoliciesResponse = z.infer<typeof listGroupPoliciesResponseSchema>;

export const groupPolicyCountsCompanySchema = z.object({
  orgId: orgIdSchema,
  access: groupPolicyAccessSchema,
  /** Null for a policy whose current version needs no acknowledgement (as on the summary). */
  items: z.array(
    z.object({
      policyId: uuidSchema,
      acknowledgements: acknowledgementCountsSchema.nullable(),
    }),
  ),
});
export const groupPolicyCountsResponseSchema = z.object({
  companies: z.array(groupPolicyCountsCompanySchema),
});
export type GroupPolicyCountsResponse = z.infer<typeof groupPolicyCountsResponseSchema>;

/** Names for the audience picker; ids belong to that company only. */
const audienceOptionSchema = z.object({ id: uuidSchema, name: z.string() });
export const companyAudienceOptionsSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  legalEntities: z.array(audienceOptionSchema),
  locations: z.array(audienceOptionSchema),
  departments: z.array(audienceOptionSchema),
  employeeGroups: z.array(audienceOptionSchema),
});
export type CompanyAudienceOptions = z.infer<typeof companyAudienceOptionsSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * Company ids to look at, comma-separated in the query (`?orgIds=a,b`), as in
 * `performance-insights.contract.ts`; a real array is accepted too. Omitted = every company of the
 * group. They only choose among the group's members — anything else is refused.
 */
const orgIdsQuerySchema = z
  .union([z.array(orgIdSchema), z.string()])
  .transform((value) => (Array.isArray(value) ? value : value.split(',').map((id) => id.trim())))
  .pipe(z.array(orgIdSchema).min(1).max(GROUP_POLICY_MAX_COMPANIES))
  .optional();

export const listGroupPoliciesQuerySchema = z.object({
  orgIds: orgIdsQuerySchema,
});

export const groupPolicyCountsQuerySchema = z.object({
  orgIds: orgIdsQuerySchema,
});

/** The PDF, staged once as soon as it is picked: base64 JSON like a single policy's upload. */
export const stageGroupPolicyUploadBodySchema = uploadCompanyPolicyDocumentBodySchema.extend({
  contentBase64: z.string().min(1).max(MAX_UPLOAD_BASE64_CHARS),
});
export type StageGroupPolicyUploadRequest = z.infer<typeof stageGroupPolicyUploadBodySchema>;

export const stagedGroupPolicyUploadSchema = z.object({
  uploadId: uuidSchema,
  fileName: z.string(),
  sizeBytes: z.number().int().positive().max(GROUP_POLICY_MAX_UPLOAD_BYTES),
});
export type StagedGroupPolicyUpload = z.infer<typeof stagedGroupPolicyUploadSchema>;

/** The policy as written once: a single-company draft body without its audience (that is per company). */
export const groupPolicyRunDraftSchema = companyPolicyDraftBodySchema.omit({ audience: true });
export type GroupPolicyRunDraft = z.infer<typeof groupPolicyRunDraftSchema>;

export const groupPolicyRunTargetSchema = z.object({
  orgId: orgIdSchema,
  /** Ids of THAT company's own legal entities, locations, departments and groups; empty = everyone. */
  audience: companyPolicyAudienceSchema.default({}),
});
export type GroupPolicyRunTarget = z.infer<typeof groupPolicyRunTargetSchema>;

/** One company of a run, as a company-wide choice: no two entries for the same company. */
const distinctOrgs = (targets: ReadonlyArray<{ orgId: string }>) =>
  new Set(targets.map((t) => t.orgId)).size === targets.length;

/** What every run says, whichever way its content is given. */
const runCommonShape = {
  /** `false` saves a draft in each company; `true` publishes it there now. */
  publish: z.boolean(),
  notify: publishCompanyPolicyBodySchema.shape.notify,
  changeNote: publishCompanyPolicyBodySchema.shape.changeNote,
};

/**
 * Form 1, a NEW policy written once (phase 1): `draft` (+ `uploadId` for a PDF) and the companies it
 * goes to. `source` must be absent, so the two forms can never be mixed.
 */
export const startGroupPolicyDraftRunBodySchema = z
  .object({
    /** Made by the client so a double-click or retry finds the copy it already created. */
    lineageId: uuidSchema,
    draft: groupPolicyRunDraftSchema,
    /** The PDF staged by `stageGroupPolicyUpload`; used when `draft.bodyFormat` is `pdf`. */
    uploadId: uuidSchema.optional(),
    source: z.undefined({ message: 'Name a draft or a source policy, not both' }).optional(),
    ...runCommonShape,
    targets: z
      .array(groupPolicyRunTargetSchema)
      .min(1)
      .max(GROUP_POLICY_MAX_COMPANIES)
      .refine(distinctOrgs, { message: 'A company can be chosen only once' }),
  })
  .refine((body) => !(body.publish && body.draft.bodyFormat === 'pdf' && !body.uploadId), {
    message: 'Upload the PDF before publishing',
    path: ['uploadId'],
  });
export type StartGroupPolicyDraftRunRequest = z.infer<typeof startGroupPolicyDraftRunBodySchema>;

/**
 * The existing policy a run copies from, in that company. `versionId` names the published version to
 * copy (e.g. one scheduled for later); omitted = the version in force today.
 */
export const groupPolicySourceSchema = z.object({
  orgId: orgIdSchema,
  policyId: uuidSchema,
  versionId: uuidSchema.optional(),
});
export type GroupPolicySource = z.infer<typeof groupPolicySourceSchema>;

/**
 * One company of a source run. What happens there is decided by the API from what that company
 * holds (never by the client, so it cannot be told to overwrite something):
 *   - `linkPolicyId` set     — that existing, unlinked policy of the company joins the lineage;
 *   - the company has a copy — the source's content is published (or saved as a draft) as that
 *                              copy's next version; `audience` omitted = KEEP its audience;
 *   - otherwise              — a new copy is created; `audience` omitted = everyone.
 * `audience: {}` is an explicit "everyone", not "keep". A linked copy keeps its own audience, so
 * `audience` and `linkPolicyId` are not allowed together.
 */
export const groupPolicyShareTargetSchema = z
  .object({
    orgId: orgIdSchema,
    audience: companyPolicyAudienceSchema.optional(),
    linkPolicyId: uuidSchema.optional(),
  })
  .refine((target) => !(target.linkPolicyId && target.audience), {
    message: 'A linked policy keeps its own audience',
    path: ['audience'],
  });
export type GroupPolicyShareTarget = z.infer<typeof groupPolicyShareTargetSchema>;

/**
 * Form 2, an EXISTING policy copied to more companies ("Add to more companies", "Update in other
 * companies"). The content is the source's current published version, so there is no `draft` and
 * no `uploadId` (a PDF is copied from the source company's storage). `lineageId` is optional: omitted
 * (or equal to the source's), the API uses the source's existing lineage, setting a new one on the
 * source first when it has none.
 */
export const startGroupPolicySourceRunBodySchema = z
  .object({
    source: groupPolicySourceSchema,
    lineageId: uuidSchema.optional(),
    /** The first day the copied version applies; omitted = today in each company's own timezone. */
    effectiveFrom: dateOnlySchema.optional(),
    draft: z.undefined({ message: 'Name a draft or a source policy, not both' }).optional(),
    uploadId: z.undefined({ message: 'A copied policy brings its own document' }).optional(),
    ...runCommonShape,
    targets: z
      .array(groupPolicyShareTargetSchema)
      .min(1)
      .max(GROUP_POLICY_MAX_COMPANIES)
      .refine(distinctOrgs, { message: 'A company can be chosen only once' }),
  })
  .refine((body) => !body.targets.some((target) => target.orgId === body.source.orgId), {
    message: 'The source company cannot also be a target',
    path: ['targets'],
  });
export type StartGroupPolicySourceRunRequest = z.infer<typeof startGroupPolicySourceRunBodySchema>;

/**
 * Exactly one of the two forms: `draft` (a new policy, optionally with `uploadId`) or `source` (an
 * existing policy). Each form refuses the other's fields, so a body with both, or a source body
 * with `uploadId`, matches neither. Narrow with `body.source ? ... : ...`.
 */
export const startGroupPolicyRunBodySchema = z.union([
  startGroupPolicyDraftRunBodySchema,
  startGroupPolicySourceRunBodySchema,
]);
export type StartGroupPolicyRunRequest = z.infer<typeof startGroupPolicyRunBodySchema>;

export const startGroupPolicyRunResponseSchema = z.object({ runId: uuidSchema });

const idempotencyHeaders = z.object({ 'idempotency-key': idempotencyKeySchema });

// ---------------------------------------------------------------------------
// Run status
// ---------------------------------------------------------------------------

/** `partial` = some companies done, some failed or skipped; `failed` = none done. */
export const groupPolicyRunStatusSchema = z.enum(['queued', 'running', 'done', 'partial', 'failed']);
export type GroupPolicyRunStatus = z.infer<typeof groupPolicyRunStatusSchema>;

export const groupPolicyRunItemStepSchema = z.enum([
  'waiting',
  'running',
  'done',
  'failed',
  'skipped',
]);
export type GroupPolicyRunItemStep = z.infer<typeof groupPolicyRunItemStepSchema>;

/**
 * What a run does in one company: `create` a new copy, publish the next version of the copy the
 * company already has (`update`), or only `link` an existing policy to the lineage.
 */
export const groupPolicyRunItemModeSchema = z.enum(['create', 'update', 'link']);
export type GroupPolicyRunItemMode = z.infer<typeof groupPolicyRunItemModeSchema>;

/** `draft`: a new policy written for the run. `source`: an existing policy copied to more companies. */
export const groupPolicyRunKindSchema = z.enum(['draft', 'source']);
export type GroupPolicyRunKind = z.infer<typeof groupPolicyRunKindSchema>;

export const groupPolicyRunItemSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  step: groupPolicyRunItemStepSchema,
  /** What happens in this company; `create` for every run written before sharing existed. */
  mode: groupPolicyRunItemModeSchema,
  /** `update` only: the company's current audience is kept instead of the one in the request. */
  keepAudience: z.boolean(),
  /** `link` only: the company's existing policy that gets the lineage id. */
  linkPolicyId: uuidSchema.nullable(),
  /** The policy created, versioned or linked in that company; set once `step` is `done`. */
  policyId: uuidSchema.nullable(),
  /** Why the company was left out, when `step` is `skipped`. */
  skipReason: groupPolicySkipReasonSchema.nullable(),
  /** A short message safe to show, when `step` is `failed`. */
  error: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  startedAt: instantSchema.nullable(),
  finishedAt: instantSchema.nullable(),
});
export type GroupPolicyRunItem = z.infer<typeof groupPolicyRunItemSchema>;

/** The policy a source run copied from. */
export const groupPolicyRunSourceSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  policyId: uuidSchema,
});
export type GroupPolicyRunSource = z.infer<typeof groupPolicyRunSourceSchema>;

/** Companies per step, for a progress line ("4 done, 1 failed"). */
export const groupPolicyRunCountsSchema = z.object({
  waiting: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type GroupPolicyRunCounts = z.infer<typeof groupPolicyRunCountsSchema>;

/** The fields a run has in every view. */
const groupPolicyRunBaseShape = {
  id: uuidSchema,
  lineageId: uuidSchema,
  kind: groupPolicyRunKindSchema,
  /** From the draft, or the source policy's title at the time of the request. */
  title: z.string(),
  /** Null on a run written for the purpose (`kind` is `draft`). */
  source: groupPolicyRunSourceSchema.nullable(),
  /** The effective date chosen for a source run; null when the run's own draft carries it. */
  effectiveFrom: dateOnlySchema.nullable(),
  publish: z.boolean(),
  notify: z.boolean(),
  status: groupPolicyRunStatusSchema,
  createdAt: instantSchema,
  finishedAt: instantSchema.nullable(),
};

export const groupPolicyRunSchema = z.object({
  ...groupPolicyRunBaseShape,
  items: z.array(groupPolicyRunItemSchema),
});
export type GroupPolicyRun = z.infer<typeof groupPolicyRunSchema>;

/** One company's result inside a run's summary line. */
export const groupPolicyRunResultSchema = z.object({
  orgId: orgIdSchema,
  orgName: z.string(),
  step: groupPolicyRunItemStepSchema,
  mode: groupPolicyRunItemModeSchema,
  policyId: uuidSchema.nullable(),
  /** The failure, else why the company was left out; null for a company that is done or still going. */
  message: z.string().nullable(),
  /** Retry can do something: it failed, or it was left alone on purpose (not for lack of rights). Absent = failed only. */
  retryable: z.boolean().optional(),
});
export type GroupPolicyRunResult = z.infer<typeof groupPolicyRunResultSchema>;

/** A row of the Recent activity list: a run without its full items, but with a result per company. */
export const groupPolicyRunSummarySchema = z.object({
  ...groupPolicyRunBaseShape,
  counts: groupPolicyRunCountsSchema,
  /** Who started it; null when that account's name cannot be read. */
  createdByName: z.string().nullable(),
  results: z.array(groupPolicyRunResultSchema),
});
export type GroupPolicyRunSummary = z.infer<typeof groupPolicyRunSummarySchema>;

export const listGroupPolicyRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListGroupPolicyRunsQuery = z.infer<typeof listGroupPolicyRunsQuerySchema>;

export const listGroupPolicyRunsResponseSchema = z.object({
  /** Newest first. */
  runs: z.array(groupPolicyRunSummarySchema),
});
export type ListGroupPolicyRunsResponse = z.infer<typeof listGroupPolicyRunsResponseSchema>;

// ---------------------------------------------------------------------------
// Bulk actions, reach preview, links
// ---------------------------------------------------------------------------

/** One copy of a policy: a company and its policy id. */
export const groupPolicyTargetSchema = z.object({ orgId: orgIdSchema, policyId: uuidSchema });
export type GroupPolicyTarget = z.infer<typeof groupPolicyTargetSchema>;

/**
 * What a bulk action can do to many copies at once. `remind`: ask everyone who has not
 * acknowledged. `archive` / `restore`: withdraw or bring back the whole policy. `publish_draft`:
 * publish the draft each copy has in progress.
 */
export const groupPolicyBulkActionSchema = z.enum(['remind', 'archive', 'restore', 'publish_draft']);
export type GroupPolicyBulkAction = z.infer<typeof groupPolicyBulkActionSchema>;

/** At most this many copies in one bulk action. */
export const GROUP_POLICY_MAX_ACTION_TARGETS = 100;
/** At most this many companies' copies in one link request. */
export const GROUP_POLICY_MAX_LINK_ITEMS = 50;

export const groupPolicyActionBodySchema = z.object({
  action: groupPolicyBulkActionSchema,
  targets: z
    .array(groupPolicyTargetSchema)
    .min(1)
    .max(GROUP_POLICY_MAX_ACTION_TARGETS)
    .refine(
      (targets) =>
        new Set(targets.map((t) => `${t.orgId}:${t.policyId}`)).size === targets.length,
      { message: 'A copy can be chosen only once' },
    ),
  /** `remind` and `publish_draft`: send the notification. Defaults on. */
  notify: publishCompanyPolicyBodySchema.shape.notify,
  /** `publish_draft` only: the change note for every version published. */
  changeNote: publishCompanyPolicyBodySchema.shape.changeNote,
});
export type GroupPolicyActionRequest = z.infer<typeof groupPolicyActionBodySchema>;

/**
 * What happened to one copy. `done`: it was acted on. `skipped`: nothing was wrong but nothing was
 * needed or allowed (no draft to publish, already archived, acknowledgement not required, no
 * access, module off); `detail` says why. `failed`: it went wrong; `detail` is a short, safe message.
 */
export const groupPolicyActionStatusSchema = z.enum(['done', 'skipped', 'failed']);
export type GroupPolicyActionStatus = z.infer<typeof groupPolicyActionStatusSchema>;

export const groupPolicyActionResultSchema = z.object({
  orgId: orgIdSchema,
  policyId: uuidSchema,
  status: groupPolicyActionStatusSchema,
  detail: z.string().optional(),
  /** `remind` and `publish_draft`: how many people were notified. */
  notified: z.number().int().nonnegative().optional(),
});
export type GroupPolicyActionResult = z.infer<typeof groupPolicyActionResultSchema>;

export const groupPolicyActionResponseSchema = z.object({
  /** One per requested copy, in the order requested. */
  results: z.array(groupPolicyActionResultSchema),
});
export type GroupPolicyActionResponse = z.infer<typeof groupPolicyActionResponseSchema>;

export const previewGroupPolicyReachBodySchema = z.object({
  targets: z
    .array(
      z.object({
        orgId: orgIdSchema,
        audience: companyPolicyAudienceSchema.default({}),
        /**
         * Count the audience that policy of the company already has (its newest published version)
         * instead of `audience`: for an update that keeps the company's audience.
         */
        policyId: uuidSchema.optional(),
      }),
    )
    .min(1)
    .max(GROUP_POLICY_MAX_COMPANIES)
    .refine(distinctOrgs, { message: 'A company can be chosen only once' }),
});
export type PreviewGroupPolicyReachRequest = z.infer<typeof previewGroupPolicyReachBodySchema>;

export const groupPolicyReachCompanySchema = z.object({
  orgId: orgIdSchema,
  access: groupPolicyAccessSchema,
  /** People in the audience; null when the company could not be read (`access` is not `ok`). */
  reach: z.number().int().nonnegative().nullable(),
  /** Everyone the company could address (the audience `{}`); null when it could not be read. */
  total: z.number().int().nonnegative().nullable(),
});
export type GroupPolicyReachCompany = z.infer<typeof groupPolicyReachCompanySchema>;

export const previewGroupPolicyReachResponseSchema = z.object({
  companies: z.array(groupPolicyReachCompanySchema),
});
export type PreviewGroupPolicyReachResponse = z.infer<typeof previewGroupPolicyReachResponseSchema>;

/**
 * Link existing copies into one row: sets the shared lineage id on each, nothing else about them
 * changes. At least two, at most one per company (a company never holds two copies of one group
 * policy). If some already share a lineage the API reuses it; copies of two different lineages
 * are refused whole with a 409 (nothing is written) rather than merged.
 */
export const linkGroupPoliciesBodySchema = z.object({
  items: z
    .array(groupPolicyTargetSchema)
    .min(2)
    .max(GROUP_POLICY_MAX_LINK_ITEMS)
    .refine(distinctOrgs, { message: 'Choose one copy per company' }),
});
export type LinkGroupPoliciesRequest = z.infer<typeof linkGroupPoliciesBodySchema>;

export const linkGroupPoliciesResponseSchema = z.object({
  lineageId: uuidSchema,
  results: z.array(
    z.object({
      orgId: orgIdSchema,
      policyId: uuidSchema,
      status: groupPolicyActionStatusSchema,
      detail: z.string().optional(),
    }),
  ),
});
export type LinkGroupPoliciesResponse = z.infer<typeof linkGroupPoliciesResponseSchema>;

/** Unlink one copy (clears its lineage id); the other copies stay linked to each other. */
export const unlinkGroupPolicyBodySchema = groupPolicyTargetSchema;
export type UnlinkGroupPolicyRequest = z.infer<typeof unlinkGroupPolicyBodySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  409: errorBodySchema,
  422: errorBodySchema,
  429: errorBodySchema,
} as const;

const entry = routeMeta('org.group:manage', { ai: 'hidden' });

const orgParams = z.object({ orgId: orgIdSchema });
const policyParams = orgParams.extend({ policyId: uuidSchema });
const versionParams = policyParams.extend({ versionId: uuidSchema });

const base = companyPolicyContract;
const inCompany = (summary: string) => `In a chosen company of the group: ${summary}`;

/**
 * Literal paths come first (see apps/api/src/lib/contract-route-order.test.ts); the mirrored routes
 * keep `company-policy.contract.ts`'s own order, which that test already holds to the same rule.
 *
 * Each mirror spreads the original route, so its method, body, query, responses and summary are the
 * original's and only the path, path parameters and metadata change. The path is a literal in every
 * entry on purpose: ts-rest derives the typed client's parameters from the path's type.
 */
export const companyGroupPolicyContract = c.router(
  {
    listGroupPolicies: {
      method: 'GET',
      path: '/',
      query: listGroupPoliciesQuerySchema,
      responses: { 200: listGroupPoliciesResponseSchema, ...errors },
      summary:
        'Policies of the chosen companies, grouped by lineage, with whether each company could be read. ' +
        'No acknowledgement counts: they come from the counts route.',
      metadata: entry,
    },
    getGroupPolicyCounts: {
      method: 'GET',
      path: '/counts',
      query: groupPolicyCountsQuerySchema,
      responses: { 200: groupPolicyCountsResponseSchema, ...errors },
      summary: 'Acknowledgement progress per company and policy (the second call of the list)',
      metadata: entry,
    },
    getCompanyAudienceOptions: {
      method: 'GET',
      path: '/companies/:orgId/audience-options',
      pathParams: orgParams,
      responses: { 200: companyAudienceOptionsSchema, ...errors },
      summary:
        "One company's legal entities, locations, departments and employee groups, for the audience picker",
      metadata: entry,
    },
    stageGroupPolicyUpload: {
      method: 'POST',
      path: '/uploads',
      body: stageGroupPolicyUploadBodySchema,
      responses: { 201: stagedGroupPolicyUploadSchema, ...errors },
      summary:
        'Stage one PDF (base64, PDF only, up to 10 MB) so a run can attach it to every company',
      metadata: entry,
    },
    startGroupPolicyRun: {
      method: 'POST',
      path: '/runs',
      headers: idempotencyHeaders,
      body: startGroupPolicyRunBodySchema,
      responses: { 202: startGroupPolicyRunResponseSchema, ...errors },
      summary:
        'Create a policy in the chosen companies (a draft in each, or published now), or copy an ' +
        'existing policy to more companies, as a background run; replies at once with the run id. ' +
        'The body is either a `draft` or a `source`, never both',
      metadata: entry,
    },
    listGroupPolicyRuns: {
      method: 'GET',
      path: '/runs',
      query: listGroupPolicyRunsQuerySchema,
      responses: { 200: listGroupPolicyRunsResponseSchema, ...errors },
      summary:
        "Her recent runs of the group, newest first (Recent activity): what went out, to which " +
        'companies, and how each company ended',
      metadata: entry,
    },
    getGroupPolicyRun: {
      method: 'GET',
      path: '/runs/:runId',
      pathParams: z.object({ runId: uuidSchema }),
      responses: { 200: groupPolicyRunSchema, ...errors },
      summary: 'A run and, per company, whether it is waiting, working, done, failed or skipped',
      metadata: entry,
    },
    retryGroupPolicyRunItem: {
      method: 'POST',
      path: '/runs/:runId/items/:orgId/retry',
      pathParams: z.object({ runId: uuidSchema, orgId: orgIdSchema }),
      body: z.object({}).default({}),
      responses: { 202: groupPolicyRunItemSchema, ...errors },
      summary: 'Run one failed company of a run again; the others are not touched',
      metadata: entry,
    },
    runGroupPolicyAction: {
      method: 'POST',
      path: '/actions',
      body: groupPolicyActionBodySchema,
      responses: { 200: groupPolicyActionResponseSchema, ...errors },
      summary:
        'Send reminders, archive, restore or publish drafts for many copies at once; replies when ' +
        'done with a result per copy (done, skipped with the reason, or failed)',
      metadata: entry,
    },
    previewGroupPolicyReach: {
      method: 'POST',
      path: '/preview',
      body: previewGroupPolicyReachBodySchema,
      responses: { 200: previewGroupPolicyReachResponseSchema, ...errors },
      summary: 'How many people each chosen audience reaches, per company, before anything is sent',
      metadata: entry,
    },
    linkGroupPolicies: {
      method: 'POST',
      path: '/links',
      body: linkGroupPoliciesBodySchema,
      responses: { 200: linkGroupPoliciesResponseSchema, ...errors },
      summary:
        'Link existing copies in different companies into one row (sets the shared group id only)',
      metadata: entry,
    },
    unlinkGroupPolicy: {
      method: 'POST',
      path: '/links/remove',
      body: unlinkGroupPolicyBodySchema,
      responses: { 200: groupPolicyActionResultSchema, ...errors },
      summary: "Unlink one company's copy from its row; the other copies stay linked",
      metadata: entry,
    },

    // ---- Mirrors of company-policy.contract.ts's administration routes, in one chosen company ----
    companyListPolicies: {
      ...base.listPolicies,
      path: '/companies/:orgId/policies',
      pathParams: orgParams,
      summary: inCompany(base.listPolicies.summary),
      metadata: entry,
    },
    companyCreatePolicy: {
      ...base.createPolicy,
      path: '/companies/:orgId/policies',
      pathParams: orgParams,
      summary: inCompany(base.createPolicy.summary),
      metadata: entry,
    },
    companyGetPolicy: {
      ...base.getPolicy,
      path: '/companies/:orgId/policies/:policyId',
      pathParams: policyParams,
      summary: inCompany(base.getPolicy.summary),
      metadata: entry,
    },
    companyRestoreVersion: {
      ...base.restoreVersion,
      path: '/companies/:orgId/policies/:policyId/versions/:versionId/restore',
      pathParams: versionParams,
      summary: inCompany(base.restoreVersion.summary),
      metadata: entry,
    },
    companyCancelScheduledVersion: {
      ...base.cancelScheduledVersion,
      path: '/companies/:orgId/policies/:policyId/versions/:versionId',
      pathParams: versionParams,
      summary: inCompany(base.cancelScheduledVersion.summary),
      metadata: entry,
    },
    companySaveDraft: {
      ...base.saveDraft,
      path: '/companies/:orgId/policies/:policyId/draft',
      pathParams: policyParams,
      summary: inCompany(base.saveDraft.summary),
      metadata: entry,
    },
    companyDiscardDraft: {
      ...base.discardDraft,
      path: '/companies/:orgId/policies/:policyId/draft',
      pathParams: policyParams,
      summary: inCompany(base.discardDraft.summary),
      metadata: entry,
    },
    companyUploadDraftDocument: {
      ...base.uploadDraftDocument,
      path: '/companies/:orgId/policies/:policyId/draft/document',
      pathParams: policyParams,
      summary: inCompany(base.uploadDraftDocument.summary),
      metadata: entry,
    },
    companyRemoveDraftDocument: {
      ...base.removeDraftDocument,
      path: '/companies/:orgId/policies/:policyId/draft/document',
      pathParams: policyParams,
      summary: inCompany(base.removeDraftDocument.summary),
      metadata: entry,
    },
    companyPublishPolicy: {
      ...base.publishPolicy,
      path: '/companies/:orgId/policies/:policyId/publish',
      pathParams: policyParams,
      summary: inCompany(base.publishPolicy.summary),
      metadata: entry,
    },
    companySetArchived: {
      ...base.setArchived,
      path: '/companies/:orgId/policies/:policyId/archive',
      pathParams: policyParams,
      summary: inCompany(base.setArchived.summary),
      metadata: entry,
    },
    companyGetVersion: {
      ...base.getVersion,
      path: '/companies/:orgId/policies/:policyId/versions/:versionId',
      pathParams: versionParams,
      summary: inCompany(base.getVersion.summary),
      metadata: entry,
    },
    companyDownloadVersionDocument: {
      ...base.downloadVersionDocument,
      path: '/companies/:orgId/policies/:policyId/versions/:versionId/document',
      pathParams: versionParams,
      summary: inCompany(base.downloadVersionDocument.summary),
      metadata: entry,
    },
    companyGetAcknowledgementReport: {
      ...base.getAcknowledgementReport,
      path: '/companies/:orgId/policies/:policyId/versions/:versionId/acknowledgements',
      pathParams: versionParams,
      summary: inCompany(base.getAcknowledgementReport.summary),
      metadata: entry,
    },
    companySendReminders: {
      ...base.sendReminders,
      path: '/companies/:orgId/policies/:policyId/remind',
      pathParams: policyParams,
      summary: inCompany(base.sendReminders.summary),
      metadata: entry,
    },
  },
  { pathPrefix: '/company-group/policies' },
);

export type CompanyGroupPolicyContract = typeof companyGroupPolicyContract;
